"use client";

import { useState, useMemo, useEffect, useCallback } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import {
  AlertTriangle,
  ArrowRight,
  Zap,
  Activity,
  Stethoscope,
  X,
  ChevronDown,
  ChevronRight,
  Search,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";

type FilterType = null | "all" | "subspecialty" | "urgent" | "semiurgent" | "redflags";

interface DiseaseData {
  slug: string;
  subspecialty: string;
  frontmatter: {
    title: string;
    category: string;
    redFlags?: string[];
    summaryCard?: {
      essence: string;
      emergencyLevel: string;
      keyTest: string;
    };
    quickGuide?: {
      todayTests?: string;
      immediateTx?: string;
      reportWhen?: string;
    };
  };
}

interface Props {
  diseases: DiseaseData[];
  subspecialtyNames: Record<string, string>;
}

function EmergencyLevelBadge({ level }: { level?: string }) {
  if (!level) return null;
  const isUrgent = level === "응급" || level === "emergency";
  const isSemiUrgent =
    level === "준응급" || level === "semi-emergency" || level.includes("준응급");
  return (
    <Badge
      variant={isUrgent ? "destructive" : isSemiUrgent ? "secondary" : "outline"}
      className={
        isUrgent
          ? "bg-clinical-red/20 text-clinical-red border-clinical-red/30"
          : isSemiUrgent
            ? "bg-clinical-amber/20 text-clinical-amber border-clinical-amber/30"
            : ""
      }
    >
      {isUrgent && <Zap className="h-3 w-3" />}
      {level}
    </Badge>
  );
}

// ── 위험 징후 카테고리 정의 ──
// "수술 관련 응급"은 가장 먼저 매칭되며, 여기에 해당하는 질환은 다른 카테고리에 중복 표시하지 않음
const SURGICAL_CATEGORY = {
  label: "수술 관련 응급", icon: "🔪",
  keywords: ["수술", "핵 낙하", "핵낙하", "후낭파열", "후낭 파열", "맥락막상출혈", "구후출혈", "TASS", "독성전안부", "수정체", "IOL", "안내렌즈", "절개", "봉합", "창상누출", "전방소실", "술 후", "수술 후"],
};

const SYMPTOM_CATEGORIES: { label: string; icon: string; keywords: string[] }[] = [
  { label: "시력저하", icon: "👁️", keywords: ["시력저하", "시력감소", "시력 급감", "시력상실", "시력소실"] },
  { label: "안압 상승", icon: "📈", keywords: ["안압", "IOP", "mmHg"] },
  { label: "통증", icon: "⚡", keywords: ["통증", "안통", "두통"] },
  { label: "RAPD / 동공이상", icon: "🔦", keywords: ["RAPD", "동공", "구심동공"] },
  { label: "전방출혈", icon: "🩸", keywords: ["전방출혈", "hyphema", "재출혈", "전방 출혈"] },
  { label: "유리체출혈", icon: "🩸", keywords: ["유리체출혈", "유리체강 출혈", "vitreous hemorrhage", "적색반사 소실"] },
  { label: "망막/맥락막하 출혈", icon: "🩸", keywords: ["망막출혈", "맥락막출혈", "황반출혈", "황반하 출혈", "망막하출혈", "망막전", "subretinal", "submacular", "다층 출혈", "화염상 출혈", "출혈"] },
  { label: "시야결손", icon: "🌗", keywords: ["시야결손", "시야장애", "커튼", "암점"] },
  { label: "각막/전안부 손상", icon: "💧", keywords: ["각막부종", "각막혼탁", "각막궤양", "각막침윤", "윤부", "화학", "천공", "용해"] },
  { label: "염증/감염", icon: "🔥", keywords: ["전방축농", "유리체 혼탁", "안내염", "봉와직염", "농양", "화농"] },
  { label: "망막/맥락막", icon: "🎯", keywords: ["망막박리", "신생혈관", "맥락막", "열공", "PVD", "NV"] },
  { label: "시신경", icon: "🧠", keywords: ["시신경", "유두부종", "유두창백", "두개내압"] },
  { label: "전신 연관", icon: "🏥", keywords: ["전신", "혈관염", "뇌종양", "해면정맥동", "CNS", "자가면역", "패혈"] },
  { label: "외상", icon: "🛡️", keywords: ["외상", "파열", "이물", "관통"] },
];

interface RedFlagEntry {
  flag: string;
  disease: DiseaseData;
}

interface SymptomGroup {
  label: string;
  icon: string;
  entries: RedFlagEntry[];
  diseaseCount: number;
}

function buildSymptomIndex(diseases: DiseaseData[]): SymptomGroup[] {
  const allEntries: RedFlagEntry[] = [];
  for (const d of diseases) {
    if (d.frontmatter.redFlags) {
      for (const flag of d.frontmatter.redFlags) {
        allEntries.push({ flag, disease: d });
      }
    }
  }

  const groups: SymptomGroup[] = [];
  const categorized = new Set<number>();

  // 1단계: 수술 관련 응급 먼저 매칭 → 해당 질환의 모든 엔트리를 전용으로 잡음
  const surgicalSlugs = new Set<string>();
  const surgicalEntries: RedFlagEntry[] = [];
  allEntries.forEach((entry, idx) => {
    const lower = entry.flag.toLowerCase();
    if (SURGICAL_CATEGORY.keywords.some((kw) => lower.includes(kw.toLowerCase()))) {
      surgicalSlugs.add(entry.disease.slug);
    }
  });
  // 수술 관련 질환의 모든 red flag를 수술 카테고리에 넣고, 다른 카테고리에서 제외
  allEntries.forEach((entry, idx) => {
    if (surgicalSlugs.has(entry.disease.slug)) {
      surgicalEntries.push(entry);
      categorized.add(idx);
    }
  });
  if (surgicalEntries.length > 0) {
    groups.push({
      label: SURGICAL_CATEGORY.label,
      icon: SURGICAL_CATEGORY.icon,
      entries: surgicalEntries,
      diseaseCount: surgicalSlugs.size,
    });
  }

  // 2단계: 나머지 카테고리 매칭 (수술 관련 질환 제외됨)
  for (const cat of SYMPTOM_CATEGORIES) {
    const entries: RedFlagEntry[] = [];
    allEntries.forEach((entry, idx) => {
      if (categorized.has(idx)) return;
      const lower = entry.flag.toLowerCase();
      if (cat.keywords.some((kw) => lower.includes(kw.toLowerCase()))) {
        entries.push(entry);
        categorized.add(idx);
      }
    });
    if (entries.length > 0) {
      const uniqueDiseases = new Set(entries.map((e) => e.disease.slug));
      groups.push({ label: cat.label, icon: cat.icon, entries, diseaseCount: uniqueDiseases.size });
    }
  }

  // 미분류 징후
  const uncategorized = allEntries.filter((_, idx) => !categorized.has(idx));
  if (uncategorized.length > 0) {
    const uniqueDiseases = new Set(uncategorized.map((e) => e.disease.slug));
    groups.push({ label: "기타 위험 징후", icon: "⚠️", entries: uncategorized, diseaseCount: uniqueDiseases.size });
  }

  // 기타→맨 끝, 수술 관련→그 앞, 나머지는 질환수 내림차순
  const LAST = "기타 위험 징후";
  return groups.sort((a, b) => {
    if (a.label === LAST) return 1;
    if (b.label === LAST) return -1;
    if (a.label === SURGICAL_CATEGORY.label) return 1;
    if (b.label === SURGICAL_CATEGORY.label) return -1;
    return b.diseaseCount - a.diseaseCount;
  });
}

function SymptomIndexView({ diseases, subspecialtyNames }: { diseases: DiseaseData[]; subspecialtyNames: Record<string, string> }) {
  const groups = useMemo(() => buildSymptomIndex(diseases), [diseases]);
  const filteredGroups = groups;
  const defaultGroup = filteredGroups.find((g) => g.label === "시력저하")?.label
    || filteredGroups[0]?.label
    || null;
  const [expandedGroup, setExpandedGroup] = useState<string | null>(defaultGroup);

  const toggleGroup = (label: string) => {
    setExpandedGroup(expandedGroup === label ? null : label);
  };

  // 선택된 그룹의 질환 목록
  const selectedGroup = expandedGroup
    ? filteredGroups.find((g) => g.label === expandedGroup)
    : null;
  const selectedDiseases = selectedGroup
    ? Array.from(new Map(selectedGroup.entries.map((e) => [e.disease.slug, e])).values())
    : [];

  return (
    <div className="space-y-4">
      {/* Symptom category chips */}
      <div className="flex flex-wrap gap-2">
        {filteredGroups.map((group) => {
          const isSelected = expandedGroup === group.label;
          return (
            <button
              key={group.label}
              onClick={() => toggleGroup(group.label)}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm border transition-all cursor-pointer ${
                isSelected
                  ? "border-clinical-red bg-clinical-red/10 text-clinical-red font-medium shadow-sm"
                  : "border-border text-muted-foreground hover:bg-accent hover:border-accent-foreground/20"
              }`}
            >
              <span>{group.icon}</span>
              <span>{group.label}</span>
              <span className={`text-xs ${isSelected ? "text-clinical-red/70" : "text-muted-foreground/60"}`}>
                {group.diseaseCount}
              </span>
            </button>
          );
        })}
      </div>

      {/* 선택된 카테고리의 질환 목록 */}
      {selectedGroup && (() => {
        // 시력저하 카테고리: 급성/비급성 서브그룹 분리
        const ACUTE_KEYWORDS = ["갑작스러운", "갑작스런", "급성", "급격한", "급감", "갑자기", "sudden"];
        const isVisionCategory = selectedGroup.label === "시력저하";

        const acuteDiseases: typeof selectedDiseases = [];
        const nonAcuteDiseases: typeof selectedDiseases = [];

        if (isVisionCategory) {
          selectedDiseases.forEach((entry) => {
            const flags = selectedGroup.entries
              .filter((e) => e.disease.slug === entry.disease.slug)
              .map((e) => e.flag);
            const hasAcute = flags.some((f) =>
              ACUTE_KEYWORDS.some((kw) => f.includes(kw))
            );
            if (hasAcute) acuteDiseases.push(entry);
            else nonAcuteDiseases.push(entry);
          });
        }

        const renderDiseaseCard = ({ disease }: { disease: DiseaseData }) => {
          const diseaseFlags = selectedGroup.entries
            .filter((e) => e.disease.slug === disease.slug)
            .map((e) => e.flag);
          const sc = disease.frontmatter.summaryCard;
          return (
            <div key={disease.slug} className="rounded-lg border bg-card p-3 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <Link
                  href={`/${disease.subspecialty}/${disease.slug}`}
                  className="group flex-1"
                >
                  <span className="text-sm font-semibold group-hover:text-clinical-red transition-colors">
                    {disease.frontmatter.title}
                  </span>
                  <span className="ml-2 text-xs text-muted-foreground">
                    {subspecialtyNames[disease.subspecialty] || disease.subspecialty}
                  </span>
                </Link>
                <EmergencyLevelBadge level={sc?.emergencyLevel} />
              </div>
              <ul className="space-y-0.5">
                {diseaseFlags.map((flag, i) => (
                  <li key={i} className="text-xs text-muted-foreground flex gap-1.5">
                    <span className="text-clinical-red mt-0.5">•</span>
                    {flag}
                  </li>
                ))}
              </ul>
              {sc?.essence && (
                <p className="text-xs text-muted-foreground/70 border-t pt-1.5 mt-1.5">
                  {sc.essence}
                </p>
              )}
            </div>
          );
        };

        return (
          <div className="space-y-3 animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="flex items-center gap-2 pb-1 border-b">
              <span className="text-lg">{selectedGroup.icon}</span>
              <h3 className="font-semibold text-sm">{selectedGroup.label}</h3>
              <span className="text-xs text-muted-foreground">{selectedGroup.diseaseCount}개 질환</span>
              <button onClick={() => setExpandedGroup(null)} className="ml-auto p-0.5 rounded hover:bg-muted">
                <X className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            </div>

            {isVisionCategory ? (
              <div className="space-y-4">
                {acuteDiseases.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Zap className="h-3.5 w-3.5 text-clinical-red" />
                      <span className="text-sm font-semibold text-clinical-red">급성 시력저하</span>
                      <span className="text-xs text-muted-foreground">{acuteDiseases.length}개 질환</span>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      {acuteDiseases.map(renderDiseaseCard)}
                    </div>
                  </div>
                )}
                {nonAcuteDiseases.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Activity className="h-3.5 w-3.5 text-clinical-amber" />
                      <span className="text-sm font-semibold text-clinical-amber">비급성 시력저하</span>
                      <span className="text-xs text-muted-foreground">{nonAcuteDiseases.length}개 질환</span>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      {nonAcuteDiseases.map(renderDiseaseCard)}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {selectedDiseases.map(renderDiseaseCard)}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

export default function EmergencyDashboard({ diseases, subspecialtyNames }: Props) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");

  // URL에서 필터 상태 복원
  const activeFilter = (searchParams.get("filter") as FilterType) || null;
  const selectedSubspecialty = searchParams.get("sub") || null;

  const updateFilter = useCallback((filter: FilterType, sub?: string | null) => {
    const params = new URLSearchParams();
    if (filter && filter !== "all") params.set("filter", filter);
    if (sub) params.set("sub", sub);
    const query = params.toString();
    router.replace(`/emergency${query ? `?${query}` : ""}`, { scroll: false });
  }, [router]);

  const subspecialties = Array.from(new Set(diseases.map((d) => d.subspecialty)));
  const urgentDiseases = diseases.filter(
    (d) => d.frontmatter.summaryCard?.emergencyLevel === "응급"
  );
  const semiUrgentDiseases = diseases.filter((d) => {
    const lv = d.frontmatter.summaryCard?.emergencyLevel;
    return lv === "준응급" || lv === "semi-emergency" || (lv && lv.includes("준응급"));
  });
  const redFlagDiseases = diseases.filter(
    (d) => d.frontmatter.redFlags && d.frontmatter.redFlags.length > 0
  );
  const symptomGroups = useMemo(() => buildSymptomIndex(diseases), [diseases]);
  const totalRedFlagCount = symptomGroups.reduce((sum, g) => sum + g.entries.length, 0);

  // Apply filter
  let filtered = diseases;
  let filterLabel = "";
  if (activeFilter === "urgent") {
    filtered = urgentDiseases;
    filterLabel = "응급";
  } else if (activeFilter === "semiurgent") {
    filtered = semiUrgentDiseases;
    filterLabel = "준응급";
  } else if (activeFilter === "redflags") {
    filtered = redFlagDiseases;
    filterLabel = "위험 징후";
  } else if (activeFilter === "subspecialty" && selectedSubspecialty) {
    filtered = diseases.filter((d) => d.subspecialty === selectedSubspecialty);
    filterLabel = subspecialtyNames[selectedSubspecialty] || selectedSubspecialty;
  }

  // 검색어 필터 (위험징후 뷰 제외)
  if (searchTerm && activeFilter !== "redflags") {
    const term = searchTerm.toLowerCase();
    filtered = filtered.filter(
      (d) =>
        d.frontmatter.title.toLowerCase().includes(term) ||
        d.frontmatter.category?.toLowerCase().includes(term) ||
        d.frontmatter.summaryCard?.essence?.toLowerCase().includes(term) ||
        d.frontmatter.redFlags?.some((f) => f.toLowerCase().includes(term))
    );
  }

  const grouped = filtered.reduce(
    (acc, d) => {
      if (!acc[d.subspecialty]) acc[d.subspecialty] = [];
      acc[d.subspecialty].push(d);
      return acc;
    },
    {} as Record<string, DiseaseData[]>
  );

  const handleStatClick = (filter: FilterType) => {
    if (activeFilter === filter) {
      updateFilter(null);
    } else {
      updateFilter(filter);
    }
  };

  const handleSubspecialtyClick = (subId: string) => {
    if (activeFilter === "subspecialty" && selectedSubspecialty === subId) {
      updateFilter(null);
    } else {
      updateFilter("subspecialty", subId);
    }
  };

  const clearFilter = () => {
    updateFilter(null);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-clinical-red/15">
          <AlertTriangle className="h-6 w-6 text-clinical-red" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">응급 질환 대시보드</h1>
          <p className="text-sm text-muted-foreground">
            당직 시 즉시 확인 — {diseases.length}개 질환 |{" "}
            {subspecialties.length}개 분과
          </p>
        </div>
      </div>

      {/* Quick Stats - Clickable */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <button
          onClick={() => handleStatClick("all")}
          className={`rounded-lg border p-3 text-center transition-all ${
            activeFilter === null || activeFilter === "all"
              ? "border-clinical-red/20 bg-clinical-red/5"
              : "border-border bg-muted/30 opacity-60"
          } cursor-pointer hover:opacity-100 hover:shadow-sm`}
        >
          <p className="text-2xl font-bold text-clinical-red">
            {diseases.length}
          </p>
          <p className="text-xs text-muted-foreground">전체</p>
        </button>

        <button
          onClick={() => handleStatClick("urgent")}
          className={`rounded-lg border p-3 text-center transition-all ${
            activeFilter === "urgent"
              ? "border-clinical-red/40 bg-clinical-red/10 ring-2 ring-clinical-red/20"
              : activeFilter === null
                ? "border-clinical-red/20 bg-clinical-red/5"
                : "border-border bg-muted/30 opacity-60"
          } cursor-pointer hover:opacity-100 hover:shadow-sm`}
        >
          <p className="text-2xl font-bold text-clinical-red">
            {urgentDiseases.length}
          </p>
          <p className="text-xs text-muted-foreground">응급</p>
        </button>

        <button
          onClick={() => handleStatClick("semiurgent")}
          className={`rounded-lg border p-3 text-center transition-all ${
            activeFilter === "semiurgent"
              ? "border-clinical-amber/40 bg-clinical-amber/10 ring-2 ring-clinical-amber/20"
              : activeFilter === null
                ? "border-clinical-amber/20 bg-clinical-amber/5"
                : "border-border bg-muted/30 opacity-60"
          } cursor-pointer hover:opacity-100 hover:shadow-sm`}
        >
          <p className="text-2xl font-bold text-clinical-amber">
            {semiUrgentDiseases.length}
          </p>
          <p className="text-xs text-muted-foreground">준응급</p>
        </button>

        <button
          onClick={() => handleStatClick("redflags")}
          className={`rounded-lg border p-3 text-center transition-all ${
            activeFilter === "redflags"
              ? "border-clinical-red/40 bg-clinical-red/10 ring-2 ring-clinical-red/20"
              : activeFilter === null
                ? "border-clinical-red/20 bg-clinical-red/5"
                : "border-border bg-muted/30 opacity-60"
          } cursor-pointer hover:opacity-100 hover:shadow-sm`}
        >
          <p className="text-2xl font-bold text-clinical-red">
            {symptomGroups.length}
          </p>
          <p className="text-xs text-muted-foreground">위험 징후</p>
        </button>
      </div>

      {/* 분과별 응급 - 별도 버튼 */}
      <button
        onClick={() => handleStatClick("subspecialty")}
        className={`w-full rounded-lg border p-3 text-center transition-all ${
          activeFilter === "subspecialty"
            ? "border-clinical-red/40 bg-clinical-red/10 ring-2 ring-clinical-red/20"
            : "border-border bg-muted/30 hover:bg-muted/50"
        } cursor-pointer hover:shadow-sm`}
      >
        <p className="text-sm font-semibold text-muted-foreground">분과별 응급</p>
      </button>

      {/* Subspecialty chips (shown when "관련 분과" is clicked) */}
      {activeFilter === "subspecialty" && (
        <div className="flex flex-wrap gap-2 animate-in fade-in slide-in-from-top-2 duration-200">
          {subspecialties.map((subId) => {
            const count = diseases.filter((d) => d.subspecialty === subId).length;
            const isSelected = selectedSubspecialty === subId;
            return (
              <button
                key={subId}
                onClick={() => handleSubspecialtyClick(subId)}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors cursor-pointer ${
                  isSelected
                    ? "border-clinical-red bg-clinical-red/15 text-clinical-red font-medium"
                    : "border-border text-muted-foreground hover:bg-accent"
                }`}
              >
                {subspecialtyNames[subId] || subId} ({count})
              </button>
            );
          })}
        </div>
      )}

      {/* Active filter indicator */}
      {activeFilter && activeFilter !== "all" && filterLabel && (
        <div className="flex items-center gap-2 text-sm animate-in fade-in duration-150">
          <Badge variant="secondary" className="bg-clinical-red/10 text-clinical-red border-clinical-red/20">
            {filterLabel}
          </Badge>
          <span className="text-muted-foreground">
            {filtered.length}개 질환
          </span>
          <button
            onClick={clearFilter}
            className="ml-1 p-0.5 rounded hover:bg-muted transition-colors"
          >
            <X className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        </div>
      )}

      {/* 위험 징후 역방향 인덱스 뷰 */}
      {activeFilter === "redflags" && (
        <SymptomIndexView diseases={diseases} subspecialtyNames={subspecialtyNames} />
      )}

      {/* 검색창 (위험징후 뷰 제외) */}
      {activeFilter !== "redflags" && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="질환명 또는 키워드 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-lg border bg-background pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-clinical-red/30"
          />
        </div>
      )}

      {/* Disease cards (redflags일 때는 숨김) */}
      {activeFilter === "redflags" ? null : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed p-12 text-center text-muted-foreground">
          응급 질환 콘텐츠가 아직 없습니다.
        </div>
      ) : (
        Object.entries(grouped).map(([subId, items]) => (
          <section key={subId} className="space-y-3">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold">
                {subspecialtyNames[subId] || subId}
              </h2>
              <Badge variant="outline" className="text-xs">
                {items.length}개
              </Badge>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {items.map((d) => {
                const sc = d.frontmatter.summaryCard;
                const qg = d.frontmatter.quickGuide;
                const redFlags = d.frontmatter.redFlags;
                return (
                  <div
                    key={d.slug}
                    className="rounded-xl border border-clinical-red/20 bg-card shadow-sm overflow-hidden"
                  >
                    {/* Card header */}
                    <div className="flex items-start justify-between gap-2 border-b border-clinical-red/10 bg-clinical-red/5 p-4">
                      <Link
                        href={`/${d.subspecialty}/${d.slug}`}
                        className="group flex-1"
                      >
                        <h3 className="font-semibold group-hover:text-clinical-red transition-colors">
                          {d.frontmatter.title}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {d.frontmatter.category}
                        </p>
                      </Link>
                      <EmergencyLevelBadge level={sc?.emergencyLevel} />
                    </div>

                    {/* Card body */}
                    <div className="p-4 space-y-3">
                      {sc?.essence && (
                        <div className="flex gap-2 text-sm">
                          <Activity className="h-4 w-4 mt-0.5 shrink-0 text-clinical-red" />
                          <p className="text-muted-foreground leading-relaxed">
                            {sc.essence}
                          </p>
                        </div>
                      )}

                      {redFlags && redFlags.length > 0 && (
                        <div className="space-y-1">
                          <p className="text-xs font-semibold text-clinical-red flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            Red Flags
                          </p>
                          <ul className="space-y-0.5">
                            {redFlags.slice(0, 3).map((flag, i) => (
                              <li
                                key={i}
                                className="text-xs text-muted-foreground flex gap-1.5"
                              >
                                <span className="text-clinical-red mt-0.5">•</span>
                                {flag}
                              </li>
                            ))}
                            {redFlags.length > 3 && (
                              <li className="text-xs text-muted-foreground/60">
                                +{redFlags.length - 3}개 더...
                              </li>
                            )}
                          </ul>
                        </div>
                      )}

                      {qg && (
                        <div className="rounded-lg bg-muted/50 p-3 space-y-1.5">
                          <p className="text-xs font-semibold flex items-center gap-1">
                            <Stethoscope className="h-3 w-3" />
                            Quick Action
                          </p>
                          {qg.todayTests && (
                            <p className="text-xs text-muted-foreground">
                              <span className="font-medium text-foreground">검사: </span>
                              {qg.todayTests}
                            </p>
                          )}
                          {qg.immediateTx && (
                            <p className="text-xs text-muted-foreground">
                              <span className="font-medium text-foreground">처치: </span>
                              {qg.immediateTx}
                            </p>
                          )}
                          {qg.reportWhen && (
                            <p className="text-xs text-muted-foreground">
                              <span className="font-medium text-foreground">보고: </span>
                              {qg.reportWhen}
                            </p>
                          )}
                        </div>
                      )}

                      <Link
                        href={`/${d.subspecialty}/${d.slug}`}
                        className="flex items-center gap-1 text-xs font-medium text-clinical-red hover:underline"
                      >
                        상세 페이지 보기
                        <ArrowRight className="h-3 w-3" />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))
      )}
    </div>
  );
}
